import { Box, CheckboxInput } from '@palmetto/palmetto-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChangeEvent, FC, useRef } from 'react';

import { DataFilterType } from 'types';

export interface DropdownOption {
  value: string;
  label: string;
  group?: string;
}

interface SearchableCheckboxFilterListProps {
  selectOptions: DropdownOption[];
  filterType: DataFilterType;
  selectedValues?: string[];
  searchValue?: string;
  handleChange: (change: ChangeEvent<HTMLInputElement>) => void;
}

const VIRTUALIZATION_THRESHOLD = 100;
const ESTIMATED_ROW_HEIGHT = 28;
const LIST_MAX_HEIGHT = 300;
const OVERSCAN = 10;

export const SearchableCheckboxFilterList: FC<SearchableCheckboxFilterListProps> = ({
  selectOptions,
  filterType,
  selectedValues,
  searchValue,
  handleChange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  let filteredOptions = selectOptions;
  if (searchValue) {
    filteredOptions = filteredOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.value.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }

  const shouldVirtualize = filteredOptions.length > VIRTUALIZATION_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  if (filterType !== DataFilterType.Select) {
    return null;
  }

  const renderCheckbox = (option: DropdownOption) => (
    <CheckboxInput
      id={option.value}
      label={option.label}
      key={option.value + option.label}
      onChange={handleChange}
      isChecked={!!selectedValues?.find((item) => item === option.value)}
    />
  );

  if (!shouldVirtualize) {
    return (
      <Box display="block" childGap="xs">
        {filteredOptions.map(renderCheckbox)}
      </Box>
    );
  }

  return (
    <div ref={scrollRef} style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
      <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const option = filteredOptions[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: 'var(--size-spacing-xs)',
              }}
            >
              {renderCheckbox(option)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
